"use client";
import React from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import StarIcon from "@mui/icons-material/Star";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const testimonials = [
  {
    nameKey: "hiringWallSection.t1.name",
    roleKey: "hiringWallSection.t1.role",
    quoteKey: "hiringWallSection.t1.quote",
    resultKey: "hiringWallSection.t1.result",
    showResult: true,
    avatarImg:
      "https://getgeek-website-assets.b-cdn.net/Getgeek%20Images/Sajid.webp",
  },
  {
    nameKey: "hiringWallSection.t2.name",
    roleKey: "hiringWallSection.t2.role",
    quoteKey: "hiringWallSection.t2.quote",
    resultKey: "hiringWallSection.t2.result",
    showResult: true,
    avatarImg:
      "https://getgeek-website-assets.b-cdn.net/Getgeek%20Images/Hadeesa.webp",
  },
  {
    nameKey: "hiringWallSection.t3.name",
    roleKey: "hiringWallSection.t3.role",
    quoteKey: "hiringWallSection.t3.quote",
    showResult: false,
    avatarImg:
      "https://getgeek-website-assets.b-cdn.net/Getgeek%20Images/Maha.webp",
  },
  {
    nameKey: "hiringWallSection.t4.name",
    roleKey: "hiringWallSection.t4.role",
    quoteKey: "hiringWallSection.t4.quote",
    showResult: false,
    avatarImg:
      "https://getgeek-website-assets.b-cdn.net/Getgeek%20Images/Moazzam.webp",
  },
];

const stats = [
  {
    valueKey: "hiringWallSection.stats.s1.value",
    labelKey: "hiringWallSection.stats.s1.label",
  },
  {
    valueKey: "hiringWallSection.stats.s2.value",
    labelKey: "hiringWallSection.stats.s2.label",
  },
  {
    valueKey: "hiringWallSection.stats.s3.value",
    labelKey: "hiringWallSection.stats.s3.label",
  },
  {
    valueKey: "hiringWallSection.stats.s4.value",
    labelKey: "hiringWallSection.stats.s4.label",
  },
  {
    valueKey: "hiringWallSection.stats.s5.value",
    labelKey: "hiringWallSection.stats.s5.label",
  },
];

const Stars = () => (
  <Box sx={{ display: "flex", gap: 0.4, mb: 2 }}>
    {[...Array(5)].map((_, i) => (
      <StarIcon
        key={i}
        sx={{ fontSize: { xs: "0.95rem", sm: "1.1rem" }, color: "#FDC700" }}
      />
    ))}
  </Box>
);

const ResultBadge = ({ text }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.8,
      backgroundColor: "#1BBE47",
      borderRadius: "6px",
      px: 1.5,
      py: 0.9,
      mb: { xs: 2, sm: 2.5 },
      width: "100%",
    }}
  >
    <Typography
      sx={{
        fontFamily: "var(--font-outfit), sans-serif",
        fontSize: { xs: "0.72rem", sm: "0.8rem" },
        fontWeight: 600,
        color: "#f0fdf4",
        lineHeight: 1.3,
      }}
    >
      {text}
    </Typography>
  </Box>
);

const HiringWallSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  return (
    <Box
      sx={{
        backgroundColor: "#1e1e30",
        py: { xs: 6, sm: 8, md: 12 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        {/* ── HEADING ── */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, sm: 5, md: 8 } }}>
          {/* Line 1 — white */}
          <Typography
            component="h2"
            sx={{
              fontFamily: "var(--font-plusjakarta), sans-serif",
              fontWeight: 800,
              fontSize: {
                xs: "1.75rem",
                sm: "2.5rem",
                md: isArabic ? "2.8rem" : "3rem",
              },
              color: "#ffffff",
              lineHeight: { xs: 1.35, md: 1.3 },
              px: { xs: 1, sm: 0 },
              mb: 0,
              whiteSpace: { md: "nowrap" },
            }}
          >
            {t("hiringWallSection.heading1")}
          </Typography>

          {/* Line 2 — gradient */}
          <Typography
            component="p"
            sx={{
              fontFamily: "var(--font-plusjakarta), sans-serif",
              fontWeight: 800,
              fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
              lineHeight: { xs: 1.35, md: 1.3 },
              background:
                "linear-gradient(90deg, #915eff 0%, #e05a78 55%, #f4a25a 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              display: "block",
              px: { xs: 1, sm: 0 },
              mt: 0,
            }}
          >
            {t("hiringWallSection.heading2")}
          </Typography>
        </Box>

        {/* ── TESTIMONIAL CARDS GRID ── */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: { xs: 2, sm: 2.5, md: 3 },
            mb: { xs: 4, sm: 5, md: 6 },
          }}
        >
          {testimonials.map((item, i) => {
            const resultText = item.showResult
              ? t(item.resultKey, { defaultValue: "" })
              : "";

            return (
              <Box
                key={i}
                sx={{
                  backgroundColor: "#2e2e45",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  borderRadius: "16px",
                  p: { xs: 2.5, sm: 3, md: 3.5 },
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "rgba(167,139,250, 0.3)",
                    backgroundColor: "#343450",
                  },
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Stars />
                  <Typography
                    sx={{
                      fontFamily: "var(--font-outfit), sans-serif",
                      fontSize: { xs: "0.82rem", sm: "0.88rem", md: "0.9rem" },
                      color: "#D1D5DC",
                      lineHeight: 1.65,
                      mb: { xs: 2, sm: 2.5 },
                    }}
                  >
                    {t(item.quoteKey)}
                  </Typography>

                  {/* Green result badge — only card 1 & 2 */}
                  {resultText && <ResultBadge text={resultText} />}
                </Box>

                {/* Avatar + Name */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1.5, sm: 2 },
                    mt: resultText ? 0 : "auto",
                    pt: resultText ? 0 : { xs: 2, sm: 2.5 },
                  }}
                >
                  <Box
                    component="img"
                    src={item.avatarImg}
                    alt={t(item.nameKey)}
                    sx={{
                      width: { xs: 36, sm: 42 },
                      height: { xs: 36, sm: 42 },
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "1px solid rgba(255,255,255,0.2)",
                      flexShrink: 0,
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "var(--font-outfit), sans-serif",
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.82rem" },
                        color: "#fff",
                      }}
                    >
                      {t(item.nameKey)}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "var(--font-outfit), sans-serif",
                        fontSize: { xs: "0.7rem", sm: "0.76rem" },
                        color: "#6A7282",
                        mt: 0.2,
                      }}
                    >
                      {t(item.roleKey)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* ── WHITE STATS BAR ── */}
        <Box
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            px: { xs: 2, sm: 3, md: 3 },
            py: { xs: 2, sm: 2.2 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(5, 1fr)" },
            alignItems: "center",
            mb: { xs: 4, sm: 5, md: 6 },
          }}
        >
          {stats.map((stat, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.6,
                py: { xs: 1.2, sm: 0 },
                px: { xs: 1, sm: 0.5 },
                gridColumn: {
                  xs: i === 4 ? "1 / -1" : "auto",
                  sm: "auto",
                },
                borderTop: {
                  xs: i >= 2 ? "1px solid rgba(0,0,0,0.08)" : "none",
                  sm: "none",
                },
                borderRight: {
                  xs:
                    i % 2 === 0 && i !== 4
                      ? "1px solid rgba(0,0,0,0.08)"
                      : "none",
                  sm:
                    i < stats.length - 1 ? "1px solid rgba(0,0,0,0.1)" : "none",
                },
              }}
            >
              {/* Bold purple value */}
              <Typography
                sx={{
                  fontFamily: "var(--font-outfit), sans-serif",
                  fontWeight: "bold",
                  fontSize: { xs: "0.95rem", sm: "1rem", md: "1.3rem" },
                  color: "#7c3aed",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                {t(stat.valueKey)}
              </Typography>
              {/* Regular dark label inline */}
              <Typography
                sx={{
                  fontFamily: "var(--font-outfit), sans-serif",
                  fontSize: { xs: "0.72rem", sm: "0.78rem", md: "0.9rem" },
                  fontWeight: 400,
                  color: "#7c3aed",
                  whiteSpace: "nowrap",
                }}
              >
                {t(stat.labelKey)}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* ── PURPLE CTA BUTTON ── */}
        <Box sx={{ textAlign: "center" }}>
          <Button
            id="btn-five"
            // onClick={() => {
            //   const el = document.getElementById("book-demo-section");
            //   if (el) el.scrollIntoView({ behavior: "smooth" });
            // }}
            onClick={() => {
              window.dispatchEvent(new Event("open-book-demo-modal"));
            }}
            endIcon={
              <ArrowForwardIcon
                sx={{
                  fontSize: "1.3rem !important",
                  ml: 0.5,
                  transform: isArabic ? "rotate(180deg)" : "none",
                  mr: 2,
                }}
              />
            }
            sx={{
              backgroundColor: "#7c3aed",
              color: "#ffffff",
              fontFamily: "var(--font-outfit), sans-serif",
              fontWeight: 700,
              fontSize: { xs: "0.75rem", sm: "0.75rem", md: "0.95rem" },
              mt: 1,
              px: { xs: 3, sm: 4, md: 5 },
              py: { xs: 1.4, sm: 1.6, md: 1.8 },
              borderRadius: "50px",
              textTransform: "none",
              width: { xs: "100%", sm: "auto" },
              boxShadow: "0px 8px 24px rgba(124, 58, 237, 0.4)",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                backgroundColor: "#6d28d9",
                transform: "scale(1.04)",
                boxShadow: "0px 12px 32px rgba(124, 58, 237, 0.5)",
              },
              "&:active": {
                transform: "scale(0.98)",
              },
            }}
          >
            {t("hiringWallSection.cta")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default HiringWallSection;
